import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../marketing/landing.css";
import "./login.css";
import { ApiError } from "../lib/auth";
import { redeemReset } from "../lib/usersApi";
import { BrandMark } from "../ui/BrandMark";
import { useDocumentTitle, vbTitle } from "../ui/useDocumentTitle";

/**
 * `/reset` — the page a reset link opens. PUBLIC by necessity: the whole point is that the
 * person cannot sign in.
 *
 * WHY IT EXISTS. `POST /api/auth/request-reset` and `POST /api/auth/reset` have both been live
 * and validating for months and neither has ever been reachable: the token was delivered by
 * email, no `VALUBRIEF_SMTP_*` is configured, and no page in this client read a token out of a
 * URL. A person who forgot their password had no route back into their own account at any
 * layer. This screen is the second half of the pair; `UsersScreen` mints the link that lands
 * here, and an admin hands it over directly.
 *
 * WHAT IT REFUSES TO DO. It does not tell the visitor whether the token is unknown, already
 * used, or expired. The server answers all three identically on purpose — each of those is a
 * fact about somebody else's account and this page is unauthenticated — so the screen repeats
 * the server's own words rather than inventing a friendlier diagnosis, and points at the person
 * who sent the link, who is the only one who can issue another.
 *
 * The password lives in React state for the duration of the submit and nowhere else. No token
 * and no password is written to storage of any kind (project-wide rule, and `lib/auth.ts`'s).
 */
export function ResetScreen() {
  useDocumentTitle(vbTitle("Choose a password"));
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* The floor is the server's (`security.PASSWORD_MIN_LENGTH` = 12). Checking it here saves a
     round trip and nothing more: the SERVER is the rule, and when the two ever disagree it is
     the server's 422 message — which names the rule — that gets shown. */
  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = !busy && token !== "" && password.length >= 12 && confirm === password;
  const note = tooShort
    ? "A password must be at least 12 characters."
    : mismatch
      ? "The two passwords do not match."
      : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await redeemReset(token, password);
      // Straight to sign-in with the good news. Every session this account had is already gone
      // server-side (SEC-02), so there is nothing left to sign out of.
      navigate("/login?reset=1", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? String(err.message)
          : "Something went wrong setting your password. Try the link again.",
      );
      setBusy(false);
    }
  }

  return (
    <div className="vb-landing vb-auth">
      <div className="auth-card">
        <a className="auth-mark" href="/">
          <BrandMark height={80} style={{ height: "var(--vb-mark-h, 80px)", width: "auto" }} />
        </a>

        {token === "" ? (
          <>
            <h1>That link is incomplete</h1>
            <p className="auth-sub">
              A reset link carries a one-time code in its address, and this one arrived without it —
              usually because it was copied by hand and the end was cut off. Ask whoever sent it to
              you for a new one.
            </p>
            <p className="auth-foot">
              <a href="/login">Back to sign in</a>
            </p>
          </>
        ) : (
          <>
            <h1>Choose a password</h1>
            <p className="auth-sub">
              This sets the password for the account this link was issued for, and signs that
              account out everywhere else.
            </p>
            <form onSubmit={submit} noValidate>
              <label htmlFor="vb-new-password">New password</label>
              <input
                id="vb-new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby="vb-reset-rule"
              />

              <label htmlFor="vb-confirm-password">Repeat it</label>
              <input
                id="vb-confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />

              {note ? (
                <p className="auth-error" role="status">
                  {note}
                </p>
              ) : null}
              {error ? (
                <p className="auth-error" role="alert">
                  {error}
                </p>
              ) : null}

              <button className="btn" type="submit" disabled={!canSubmit}>
                {busy ? "Setting your password…" : "Set password"}
              </button>
            </form>
            <p id="vb-reset-rule" className="auth-foot">
              At least 12 characters. Nothing else is required — length is what protects an
              account, and a rule that forces a symbol mostly produces the same password with an
              exclamation mark on the end.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default ResetScreen;
